// Fatigue Management — operational fatigue-risk dashboard.
//
// PEAK4-owned derived indicators (FED, LRM, CDI, PDC) built on wearable data,
// across six sections. Cohort-level by default — this is operational insight
// and intervention, not individual surveillance. Currently driven by the mock
// data layer in ../data/fatigueService.ts pending the algorithm engine.

import React, { useState } from "react";
import {
  Box,
  Breadcrumbs,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import WatchRoundedIcon from "@mui/icons-material/WatchRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import { OverviewSection } from "../sections/OverviewSection";
import {
  ActionsSection,
  CohortRiskSection,
  MethodologySection,
  OperationsSection,
  WearComplianceSection,
} from "../sections/sections";
import { CohortDetailDrawer } from "../components/CohortDetailDrawer";
import { MetricDefinitionDrawer } from "../components/MetricDefinitionDrawer";
import type { DerivedMetricKey } from "../data/types";

type SectionKey =
  | "overview"
  | "operations"
  | "cohorts"
  | "actions"
  | "compliance"
  | "methodology";

const NAV: { key: SectionKey; label: string; icon: React.ReactElement }[] = [
  { key: "overview", label: "Overview", icon: <DashboardRoundedIcon fontSize="small" /> },
  { key: "operations", label: "Operations / Safety", icon: <HealthAndSafetyRoundedIcon fontSize="small" /> },
  { key: "cohorts", label: "Cohort Risk", icon: <GroupsRoundedIcon fontSize="small" /> },
  { key: "actions", label: "Wellbeing Actions", icon: <ChecklistRoundedIcon fontSize="small" /> },
  { key: "compliance", label: "Wear Compliance", icon: <WatchRoundedIcon fontSize="small" /> },
  { key: "methodology", label: "Methodology", icon: <MenuBookRoundedIcon fontSize="small" /> },
];

export const FatigueManagement: React.FC = () => {
  const theme = useTheme();
  const [section, setSection] = useState<SectionKey>("overview");
  const [cohortId, setCohortId] = useState<string | null>(null);
  const [metricKey, setMetricKey] = useState<DerivedMetricKey | null>(null);

  const activeLabel = NAV.find((n) => n.key === section)?.label ?? "";

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link underline="hover" color="inherit" href="#" onClick={(e) => e.preventDefault()}>
          Dashboards
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setSection("overview");
          }}
        >
          Fatigue Management
        </Link>
        <Typography color="text.primary">{activeLabel}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "232px 1fr" }, gap: 3 }}>
        {/* Left sub-navigation */}
        <Box component="nav" aria-label="Fatigue Management sections">
          <List
            sx={{
              position: { md: "sticky" },
              top: 16,
              borderRadius: 2,
              p: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            {NAV.map((item) => {
              const active = item.key === section;
              return (
                <ListItemButton
                  key={item.key}
                  selected={active}
                  onClick={() => setSection(item.key)}
                  aria-current={active ? "page" : undefined}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                      "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.22) },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: active ? "primary.main" : "text.secondary" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: active ? 800 : 500,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        {/* Section content */}
        <Box sx={{ minWidth: 0 }}>
          {section === "overview" && <OverviewSection onOpenCohort={setCohortId} />}
          {section === "operations" && <OperationsSection onOpenCohort={setCohortId} />}
          {section === "cohorts" && <CohortRiskSection onOpenCohort={setCohortId} />}
          {section === "actions" && <ActionsSection />}
          {section === "compliance" && <WearComplianceSection />}
          {section === "methodology" && <MethodologySection onOpenMetric={setMetricKey} />}
        </Box>
      </Box>

      <CohortDetailDrawer cohortId={cohortId} onClose={() => setCohortId(null)} />
      <MetricDefinitionDrawer
        open={metricKey !== null}
        metricKey={metricKey}
        onClose={() => setMetricKey(null)}
      />
    </Box>
  );
};
